import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { checkIsOverstay, fetchPassInfo, getPassLimitMs, normalizeAccessLog, officerLabel } from "../utils/accessLogs";
import { useRateLimit } from "../utils/rateLimit";
import { normalizeMaintenanceRequest, normalizeCommunicationNote } from "../utils/enums";
import { ShieldCheck, Clock, AlertTriangle, Shield } from "lucide-react";
import Sidebar, { clearSession } from "../components/Sidebar";

const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? "/api" : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

const PAGE_SIZE = 20;

const STATUS_META = {
  Created: { badge: "badge-light-info", icon: "ki-information-5", label: "Created" },
  "In Progress": { badge: "badge-light-warning", icon: "ki-time", label: "In Progress" },
  Completed: { badge: "badge-light-success", icon: "ki-check-circle", label: "Completed" },
  Canceled: { badge: "badge-light-danger", icon: "ki-cross-circle", label: "Canceled" },
};

const STATUS_TO_INT = { Created: 0, "In Progress": 1, Completed: 2, Canceled: 3 };

const PRIORITY_META = {
  Low: { badge: "badge-light-info" },
  Normal: { badge: "badge-light-secondary" },
  High: { badge: "badge-light-warning" },
  Emergency: { badge: "badge-light-danger" },
};

const SECURITY_CATEGORIES = [
  "Access Control",
  "CCTV / Surveillance",
  "Fire Safety",
  "Security",
  "Visitor Management"
];

function safeJsonParse(t) { try { return JSON.parse(t); } catch { return null; } }
function getSession() { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } }
function setSession(s) { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }
export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, ms = 60000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try {
    const signal = options.signal
      ? (() => { const merged = new AbortController(); options.signal.addEventListener("abort", () => merged.abort()); ctrl.signal.addEventListener("abort", () => merged.abort()); return merged.signal; })()
      : ctrl.signal;
    return await fetch(url, { ...options, signal });
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Request timed out.");
    throw e;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const r = await refreshAccessToken();
      token = r.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

function getDuration(start, end) {
  if (!start || !end) return "—";
  const diff = new Date(end) - new Date(start);
  if (diff < 0) return "—";
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) return `${hours} hr${hours > 1 ? "s" : ""} ${mins} min${mins !== 1 ? "s" : ""}`;
  return `${mins} min${mins !== 1 ? "s" : ""}`;
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const VisitProgressBar = ({ checkIn, checkOut, isActive, limitAt = null }) => {
  const [elapsed, setElapsed] = useState("");
  const [progressPct, setProgressPct] = useState(0);
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    if (!checkIn) return;
    const start = new Date(checkIn).getTime();
    
    if (!isActive && checkOut) {
       setElapsed(getDuration(checkIn, checkOut));
       setProgressPct(100);
       return;
    }
    
    const update = () => {
      const now = new Date().getTime();
      const diff = Math.max(0, now - start);
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      
      let timeStr = "";
      if (hours > 0) timeStr += `${hours} hr${hours > 1 ? "s" : ""} `;
      timeStr += `${mins} min${mins !== 1 ? "s" : ""}`;
      setElapsed(timeStr.trim());
      
      const allowed = limitAt ? Math.max(1, limitAt - start) : 12 * 3600000;
      let pct = (diff / allowed) * 100;
      setProgressPct(Math.min(100, Math.max(5, pct)));
      setIsOverdue(Boolean(isActive && limitAt && now > limitAt));
    };
    
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [checkIn, checkOut, isActive, limitAt]);

  if (!checkIn) return null;

  return (
    <div className="mt-1 mb-2">
      <div className="d-flex flex-stack mb-2">
        <span className="fs-8 fw-semibold text-gray-500 text-uppercase tracking-wider">
          {isActive ? "Time Elapsed" : "Total Duration"}
        </span>
        <span className={`fs-7 fw-bolder ${isActive ?'text-danger' : 'text-success'}`}>
          {elapsed}
        </span>
      </div>
      <div className="progress h-8px bg-light w-100 rounded-pill overflow-hidden">
        <div 
          className={`progress-bar rounded-pill ${isActive ?'bg-danger progress-bar-striped progress-bar-animated' : 'bg-success'}`}
          role="progressbar"
          style={{ width: `${isActive ? 100 : progressPct}%`, transition: 'width 1s ease' }}
        ></div>
      </div>
      {isActive && (
        <div className="d-flex align-items-center justify-content-end gap-1 mt-2">
          {isOverdue ? (
            <>
              <i className="ki-outline ki-notification-status text-danger fs-5 animate-pulse"></i>
              <span className="fs-9 text-danger fw-bold text-uppercase tracking-wider">Pass time limit exceeded</span>
            </>
          ) : (
            <>
              <span className="spinner-grow spinner-grow-sm text-danger opacity-75" style={{width: '0.65rem', height: '0.65rem'}}></span>
              <span className="fs-9 text-danger fw-bold text-uppercase tracking-wider">Visitor inside</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// --- MODALS ---

function GateActionModal({ modalRef, actionType, estateId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", accessPoint: "Main Gate" });
  const isCheckIn = actionType === "in";
  const { hide } = useBootstrapModal(modalRef);

  const resetAndClose = () => {
    setForm({ code: "", accessPoint: "Main Gate" });
    hide();
  };

  const waitSeconds = useRateLimit(`POST /estates/:id/access-logs/${isCheckIn ? "check-in" : "check-out"}`);

  const handleSubmit = async () => {
    if (waitSeconds > 0) return;
    if (!form.code.trim()) return toast.error("Please enter the access code.");
    if (isCheckIn && !form.accessPoint.trim()) return toast.error("Access point is required.");
    setLoading(true);
    try {
      const endpoint = isCheckIn ? "check-in" : "check-out";
      const body = isCheckIn
        ? { code: form.code.trim(), verificationMethod: 0, accessPoint: form.accessPoint }
        : { code: form.code.trim() };
      const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs/${endpoint}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errObj = safeJsonParse(await res.text());
        throw new Error(errObj?.message || errObj?.title || `Failed to check ${actionType}`);
      }
      toast.success(`Successfully checked ${isCheckIn ? "in" : "out"}!`);
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade" ref={modalRef} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-sm animate-modal">
        <div className="modal-content custom-card-modal overflow-hidden border-0">
          <div className="modal-header bg-white border-bottom border-gray-200 px-8 py-5">
            <h3 className="text-gray-900 fw-bold mb-1 fs-3">{isCheckIn ? "Manual Check-In" : "Manual Check-Out"}</h3>
            <button type="button" className="btn btn-sm btn-icon btn-light btn-close-danger" onClick={resetAndClose}>
              <i className="ki-outline ki-cross fs-2 text-gray-500"></i>
            </button>
          </div>
          <div className="modal-body p-6">
            <div className="mb-4">
              <label className="form-label fs-8 fw-semibold text-gray-600">Access Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control form-control-solid fw-bold text-center fs-3"
                placeholder="e.g. ABC123"
                value={form.code}
                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
              />
            </div>
            {isCheckIn && (
              <div className="mb-2">
                <label className="form-label fs-8 fw-semibold text-gray-600">Access Point <span className="text-danger">*</span></label>
                <input type="text" className="form-control form-control-sm" value={form.accessPoint} onChange={e => setForm(p => ({ ...p, accessPoint: e.target.value }))} />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light-danger btn-sm" onClick={resetAndClose}>Cancel</button>
            <button type="button" className={`btn btn-${isCheckIn ?"success" : "danger"} btn-sm`} onClick={handleSubmit} disabled={loading || waitSeconds > 0}>
              {loading ? <span className="spinner-border spinner-border-sm me-1"></span> : waitSeconds > 0 ? `Try again in ${waitSeconds}s` : (isCheckIn ? "Check In Visitor" : "Check Out Visitor")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessDetailModal({ modalRef, selectedItem, owner, passInfo }) {
  const { hide } = useBootstrapModal(modalRef);

  if (!selectedItem) return null;

  return (
    <div className="modal fade" ref={modalRef} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl animate-modal">
       <div className="modal-content custom-card-modal overflow-hidden border-0">
          <div className="modal-header bg-white border-bottom border-gray-200 px-8 py-5">
            <h3 className="text-gray-900 fw-bold mb-1 fs-3">Access Log Details</h3>
            <button type="button" className="btn btn-sm btn-icon btn-light btn-close-danger" onClick={hide}>
              <i className="ki-outline ki-cross fs-2 text-gray-500"></i>
            </button>
          </div>

          {checkIsOverstay(selectedItem, passInfo) && (
            <div className="alert alert-danger d-flex align-items-center p-4 mb-0 rounded-0 border-0 border-bottom">
              <i className="ki-outline ki-information-5 fs-2hx text-danger me-4"></i>
              <div className="d-flex flex-column">
                <h4 className="mb-1 text-danger">Pass Time Limit Exceeded</h4>
                <span>This dependant's temporary pass expired {new Date(getPassLimitMs(selectedItem, passInfo)).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} and they are still inside. Please contact the host.</span>
              </div>
            </div>
          )}

          <div className="modal-body p-0">
            <div className="row g-0">
              <div className="col-12 col-lg-7 p-8 border-end">
                <div className="d-flex align-items-start gap-4 mb-6">
                  <div className="symbol symbol-60px symbol-circle flex-shrink-0">
                    <span className="symbol-label bg-light-primary text-primary fw-bold fs-4">
                      {getInitials(selectedItem.dependentName || selectedItem.visitorName || "Guest")}
                    </span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold text-gray-900 fs-4 mb-1">
                      {selectedItem.dependentName || selectedItem.visitorName || "Unknown Visitor"}
                    </div>
                    <div className="text-gray-600 fw-semibold fs-7 mb-3">
                      Code: <span className="fw-bold text-gray-900 bg-light px-2 py-1 rounded">{selectedItem.code || "—"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className={`badge badge-light-${selectedItem.isActiveVisit ?"success" : "secondary"}`}>
                        {selectedItem.isActiveVisit ? "Inside Estate" : "Checked Out"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row g-4 mt-2 mb-6">
                  <div className="col-12 col-md-6">
                    <div className="fw-semibold text-gray-500 fs-8 text-uppercase tracking-wider mb-1">Access Point</div>
                    <div className="fw-bold text-gray-900 fs-6">{selectedItem.accessPoint || "—"}</div>
                    <div className="text-muted fs-8 mt-1">Method: {selectedItem.verificationMethod || "—"}</div>
                  </div>
                  <div className="col-12 col-md-6 border-start border-gray-200 ps-md-6">
                    <VisitProgressBar 
                      checkIn={selectedItem.checkedInAt} 
                      checkOut={selectedItem.checkedOutAt} 
                      isActive={selectedItem.isActiveVisit} 
                      limitAt={getPassLimitMs(selectedItem, passInfo)}
                    />
                  </div>
                </div>

                <div className="fw-bold text-gray-900 mb-4 fs-6 mt-8 border-top pt-6">Host / Tenant Info</div>
                <div className="d-flex align-items-start gap-4">
                  <div className="symbol symbol-60px symbol-circle flex-shrink-0">
                    {owner?.avatarUrl ? (
                      <img src={owner.avatarUrl} alt="Owner" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="symbol-label bg-light-primary text-primary fw-bold fs-4">
                        {String(owner?.fullName || "?").slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold text-gray-900 fs-4 mb-1">
                      {owner?.title ? `${owner.title} ` : ""}
                      {owner?.fullName || "Unknown Host"}
                    </div>
                    <div className="text-gray-600 fw-semibold fs-7 mb-3">
                      {owner?.building || "—"}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <a className="btn btn-sm btn-light-primary" href={owner?.phone ? `tel:${owner.phone}` : "#"} onClick={e => !owner?.phone && e.preventDefault()}>
                        <i className="ki-outline ki-call fs-4 me-2"></i> Call
                      </a>
                      <a className="btn btn-sm btn-light-success" href={owner?.phone ? `https://wa.me/${owner.phone.replace(/\D/g, "")}` : "#"} target="_blank" rel="noreferrer" onClick={e => !owner?.phone && e.preventDefault()}>
                        <i className="ki-outline ki-message-text-2 fs-4 me-2"></i> WhatsApp
                      </a>
                      {owner?.id && (
                        <NavLink to={`/tenants/${owner.id}`} className="btn btn-sm btn-light-info">
                          <i className="ki-outline ki-user fs-4 me-1"></i> View Profile
                        </NavLink>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-5 p-8">
                <div className="fw-bold text-gray-900 mb-4 fs-6">Timeline Events</div>
                
                <div className="mt-4 p-4 rounded bg-light mb-4">
                  <div className="fs-8 text-gray-500 mb-1 text-uppercase">Checked In</div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge badge-light-success fw-bold">IN</span>
                    <span className="fs-6 text-gray-900 fw-bold">
                      {selectedItem.checkedInAt ? new Date(selectedItem.checkedInAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </span>
                  </div>
                  {selectedItem.checkedInAt && <div className="text-muted fs-8 mt-1">{officerLabel(selectedItem, "in")}</div>}
                </div>

                <div className="p-4 rounded bg-light">
                  <div className="fs-8 text-gray-500 mb-1 text-uppercase">Checked Out</div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge badge-light-danger fw-bold">OUT</span>
                    <span className="fs-6 text-gray-900 fw-bold">
                      {selectedItem.checkedOutAt ? new Date(selectedItem.checkedOutAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </span>
                  </div>
                  {selectedItem.checkedOutAt && <div className="text-muted fs-8 mt-1">{officerLabel(selectedItem, "out")}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light-danger btn-sm" onClick={hide}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IncidentDetailModal({ modalRef, estateId, request, onStatusChange }) {
  const [fullRequest, setFullRequest] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const notesEndRef = useRef(null);
  const { hide } = useBootstrapModal(modalRef);

  useEffect(() => {
    if (!request?.id || !estateId) return;
    setLoading(true);
    setFullRequest(request);
    setNotes([]);

    Promise.all([
      authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}`).then(r => r.ok ? r.json() : null),
      authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes?pageIndex=1&pageSize=100`).then(r => r.ok ? r.json() : null),
    ])
      .then(([reqData, notesData]) => {
        const fd = reqData?.data || {};
        const titleStr = fd?.requesterTitle || request?.requesterTitle ? `${fd?.requesterTitle || request?.requesterTitle} ` : "";
        const fullName = fd?.requesterName ? `${titleStr}${fd.requesterName}`.trim() : request?.tenantName;
        
        setFullRequest({
          ...request, ...normalizeMaintenanceRequest(fd),
          tenantProfilePictureUrl: fd?.requesterImageUrl || request?.tenantProfilePictureUrl,
          tenantName: fullName,
        });
        setNotes(parseArrayData(notesData).map(normalizeCommunicationNote));
      })
      .catch(() => toast.error("Failed to load details."))
      .finally(() => setLoading(false));
  }, [request?.id, estateId]);

  useEffect(() => {
    if (notes.length) notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [notes]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes`, {
        method: "POST", body: JSON.stringify({ message: replyText.trim() }),
      });
      if (!res.ok) throw new Error("Failed to send reply.");
      setReplyText("");
      const updated = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes?pageIndex=1&pageSize=100`);
      if (updated.ok) setNotes(parseArrayData(await updated.json()).map(normalizeCommunicationNote));
    } catch (err) { toast.error(err.message || "Failed to send reply."); }
    finally { setSendingReply(false); }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!newStatus || newStatus === fullRequest?.status) return;
    setUpdatingStatus(true);
    const statusPayload = STATUS_TO_INT[newStatus] ?? 0;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/status`, {
        method: "PATCH", body: JSON.stringify({ status: statusPayload }),
      });
      if (!res.ok) throw new Error("Failed to update status.");
      setFullRequest(prev => ({ ...prev, status: newStatus }));
      onStatusChange?.(request.id, newStatus);
      toast.success(`Incident status updated to ${newStatus}.`);
    } catch (err) { toast.error(err.message || "Failed to update status."); }
    finally { setUpdatingStatus(false); }
  };

  const d = fullRequest || request;
  if (!d) return null;

  const sm = STATUS_META[d.status] || STATUS_META["Created"];
  const pm = PRIORITY_META[d.priorityLevel || d.priority] || PRIORITY_META["Normal"];

  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl animate-modal">
        <div className="modal-content custom-card-modal overflow-hidden border-0">
          <div className="modal-header bg-white border-bottom border-gray-200 px-8 py-5">
            <h3 className="text-gray-900 fw-bold mb-1 fs-3">Security Incident Report</h3>
            <button type="button" className="btn btn-sm btn-icon btn-light btn-close-danger" onClick={hide}>
              <i className="ki-outline ki-cross fs-2 text-gray-500"></i>
            </button>
          </div>
          <div className="modal-body p-0">
            <div className="row g-0">
              <div className="col-12 col-lg-7 p-8 border-end">
                <div className="d-flex align-items-start gap-4 mb-6">
                  <div className="symbol symbol-60px symbol-circle flex-shrink-0">
                    {d.tenantProfilePictureUrl ? (
                      <img src={d.tenantProfilePictureUrl} alt={d.tenantName} />
                    ) : (
                      <span className="symbol-label bg-light-danger text-danger fs-4 fw-bold">{getInitials(d.tenantName)}</span>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold text-gray-900 fs-4 mb-1">{d.tenantName || "Unknown Reporter"}</div>
                    <div className="text-gray-600 fw-semibold fs-7 mb-3">{d.buildingNumber || d.houseNumber || "Location Unspecified"}</div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="fw-bold text-gray-900 mb-2 fs-5">{d.title}</div>
                  <div className="fs-7 text-gray-600 lh-lg bg-light p-4 rounded">
                    {loading && !d.description ? "Loading details..." : d.description || d.shortDescription || "No further details provided."}
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-4 pt-6 border-top border-gray-200">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="badge badge-light-dark fs-8">{d.category}</span>
                    <span className={`badge fs-8 ${pm.badge}`}>{d.priorityLevel || d.priority || "Normal"}</span>
                    <span className={`badge fs-8 ${sm.badge}`}><i className={`ki-outline ${sm.icon} me-1 fs-9`}></i>{sm.label}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-8 text-gray-500 fw-bold">Update Status:</span>
                    <select className="form-select form-select-sm w-150px" value={d.status} onChange={e => handleUpdateStatus(e.target.value)} disabled={updatingStatus}>
                      <option value="Created">Reported</option>
                      <option value="In Progress">Investigating</option>
                      <option value="Completed">Resolved</option>
                      <option value="Canceled">Dismissed</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-5 p-8 max-h-500px overflow-y-auto bg-light-secondary opacity-75">
                <div className="fw-bold text-gray-900 mb-4 fs-6">Incident Comms & Logs</div>
                <div className="d-flex flex-column h-350px bg-white border border-gray-200 rounded">
                  <div className="flex-grow-1 p-4 overflow-y-auto">
                    {loading ? (
                      <div className="text-center py-4 text-gray-400 fs-7">Loading thread...</div>
                    ) : notes.length === 0 ? (
                      <div className="text-center py-6 text-gray-400 fs-7">No communication yet.</div>
                    ) : (
                      notes.map((note, i) => {
                        const isAdmin = note.senderType?.toLowerCase().includes("admin");
                        return (
                          <div key={note.id || i} className={`d-flex gap-3 mb-4 ${isAdmin ?"flex-row-reverse" : ""}`}>
                            <div className="symbol symbol-35px symbol-circle flex-shrink-0">
                              <span className={`symbol-label ${isAdmin ?"bg-light-primary text-primary" : "bg-light-dark text-dark"}`}>
                                <i className={`ki-outline ${isAdmin ?"ki-shield-tick" : "ki-user"} fs-6`}></i>
                              </span>
                            </div>
                            <div className={`d-flex flex-column ${isAdmin ?"align-items-end" : "align-items-start"}`}>
                              <span className="fs-8 text-muted mb-1">{note.senderName} · {fmtDateTime(note.createdDate)}</span>
                              <div className={`p-3 rounded fs-7 ${isAdmin ?"bg-light-primary text-primary" : "bg-light text-dark"}`}>
                                {note.message}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={notesEndRef} />
                  </div>
                  <div className="p-3 border-top bg-light">
                    <textarea className="form-control form-control-sm mb-2 resize-none" rows={2} placeholder="Add log or reply to reporter..." value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleSendReply(); }} />
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fs-9 text-gray-400">Ctrl + Enter to send</span>
                      <button className="btn btn-dark btn-sm" onClick={handleSendReply} disabled={!replyText.trim() || sendingReply}>
                        {sendingReply ? "Sending..." : "Submit Log"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Time only for today's check-ins; older ones (most overstays) also need the date.
function fmtCheckIn(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return d.toDateString() === new Date().toDateString()
    ? time
    : `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}, ${time}`;
}

// --- MAIN PAGE COMPONENT ---

export default function SecurityIncidents() {
  const session = getSession();
  const estateId = session?.estateId || "";

  const [pageReady, setPageReady] = useState(false);

  // Active / Overtime Stays State (Col-4)
  const [activeLogs, setActiveLogs] = useState([]);
  const [passInfo, setPassInfo] = useState({});
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSearch, setAccessSearch] = useState("");
  const [accessTotals, setAccessTotals] = useState({ inside: 0, overstays: 0 });

  // Security Incidents State (Col-8)
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incidentFilters, setIncidentFilters] = useState({ search: "", status: "all", priority: "all" });

  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedAccessLog, setSelectedAccessLog] = useState(null);

  const checkInModalRef = useRef(null);
  const checkOutModalRef = useRef(null);
  
  const incidentModalRef = useRef(null);
  const incidentModal = useBootstrapModal(incidentModalRef);
  
  const accessDetailModalRef = useRef(null);
  const accessDetailModal = useBootstrapModal(accessDetailModalRef);

  const checkInModal = useBootstrapModal(checkInModalRef);
  const checkOutModal = useBootstrapModal(checkOutModalRef);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.className = "app-default";
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    html.setAttribute("data-bs-theme", localStorage.getItem("kt_theme_mode") || "light");

    const init = () => {
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 100);
    return () => clearTimeout(t);
  }, []);

  const fetchActiveLogs = useCallback(async () => {
    if (!estateId) return;
    setAccessLoading(true);
    try {
      // Everyone inside, and separately everyone the API flags as overstayed, so overstays
      // aren't lost past the first page and are listed first.
      const load = async (filter) => {
        const qp = new URLSearchParams({ pageIndex: "1", pageSize: "100", ...filter });
        if (accessSearch) qp.append("search", accessSearch);
        const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?${qp.toString()}`);
        if (!res.ok) throw new Error("Failed to load active access logs");
        const json = await res.json();
        return { logs: parseArrayData(json).map(normalizeAccessLog), total: json?.data?.totalCount ?? 0 };
      };
      const [inside, overstays] = await Promise.all([load({ isActiveVisit: "true" }), load({ isOverstay: "true" })]);
      const seen = new Set(overstays.logs.map((l) => l.id));
      setActiveLogs([...overstays.logs, ...inside.logs.filter((l) => !seen.has(l.id))]);
      setAccessTotals({ inside: inside.total, overstays: overstays.total });
    } catch (err) { toast.error(err.message); }
    finally { setAccessLoading(false); setPageReady(true); }
  }, [estateId, accessSearch]);

  const fetchIncidents = useCallback(async () => {
    if (!estateId) return;
    setIncidentsLoading(true);
    try {
      const searchQ = incidentFilters.search ? `&search=${encodeURIComponent(incidentFilters.search.trim())}` : "";
      // Fetching incidents (using maintenance request endpoint filtered by security category)
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests?pageIndex=0&pageSize=100${searchQ}`);
      const json = await res.json();
      
      let allRequests = parseArrayData(json).map(normalizeMaintenanceRequest);
      allRequests = allRequests.filter(r => SECURITY_CATEGORIES.some(sc => r.category?.includes(sc) || r.title?.includes(sc)));

      if (incidentFilters.status !== "all") {
        allRequests = allRequests.filter(r => r.status === incidentFilters.status);
      }
      if (incidentFilters.priority !== "all") {
        allRequests = allRequests.filter(r => r.priorityLevel === incidentFilters.priority);
      }

      setIncidents(allRequests);
    } catch (err) { toast.error("Failed to load security incidents"); }
    finally { setIncidentsLoading(false); }
  }, [estateId, incidentFilters]);

  useEffect(() => {
    fetchActiveLogs();
    fetchIncidents();
  }, [fetchActiveLogs, fetchIncidents]);

  const handleIncidentStatusChange = (id, status) => {
    setIncidents(prev => prev.map(r => r.id === id ? { ...r, status } : r));
  };

  // Overstays come flagged by the API; the pass expiry (for the visit bar) is looked up
  // only when someone opens a visit, not for every row on load.
  const handleAccessLogClick = (log) => {
    setSelectedAccessLog(log);
    accessDetailModal.show();
    if (!passInfo[log.id]) {
      fetchPassInfo({ apiBase: API_BASE, estateId, logs: [log], authFetch })
        .then((found) => { if (Object.keys(found).length) setPassInfo((prev) => ({ ...prev, ...found })); });
    }
  };

  const stats = useMemo(() => {
    const activeInside = accessTotals.inside;
    const overstayCount = accessTotals.overstays;
    const openIncidents = incidents.filter(i => i.status === "Created" || i.status === "In Progress").length;
    return { activeInside, overstayCount, openIncidents, totalIncidents: incidents.length };
  }, [accessTotals, incidents]);

  return (
    <>
      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header" data-kt-sticky="true">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>

              <div className="d-flex flex-stack flex-lg-row-fluid">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3 mb-0">
                  <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-2">
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">
                      <Link to="/dashboard" className="text-gray-700 text-hover-primary me-1"><i className="ki-outline ki-home text-gray-700 fs-6"></i></Link>
                    </li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">Security Command</li>
                  </ul>
                  <h1 className="text-gray-900 fw-bolder fs-3 m-0">Security & Access Management</h1>
                </div>

                <div className="d-flex gap-2">
                  <button className="btn btn-dark d-flex flex-center h-35px h-lg-40px" onClick={() => checkInModal.show()}>
                    <i className="ki-outline ki-entrance-left fs-3 text-white"></i>
                    <span className="d-none d-sm-inline ps-2">Manual Check-In</span>
                  </button>
                  <button className="btn btn-light-danger d-flex flex-center h-35px h-lg-40px" onClick={() => checkOutModal.show()} title="Manual Check-Out">
                    <i className="ki-outline ki-entrance-right fs-3"></i>
                    <span className="d-none d-sm-inline ps-2">Manual Check-Out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    {/* Premium Security Gradient Banner */}
                    <div className="card card-flush mb-6 border-0" style={{ background: "linear-gradient(135deg, #065F46 0%, #0F9D58 45%, #12B76A 100%)", borderRadius: "16px" }}>
                      <div className="card-body p-0">
                        <div className="row g-0">
                          {[
                            { icon: ShieldCheck, label: "Active Visitors Inside", value: stats.activeInside, sub: "Checked in visitors" },
                            { icon: Clock, label: "Overstay Alerts", value: stats.overstayCount, sub: "Temporary passes expired" },
                            { icon: AlertTriangle, label: "Open Incidents", value: stats.openIncidents, sub: "Pending review" },
                            { icon: Shield, label: "Total Security Incidents", value: stats.totalIncidents, sub: "Reported incidents" },
                          ].map((item, i) => {
                            const IconComponent = item.icon;
                            return (
                              <div key={item.label} className="col-12 col-md-6 col-xl-3">
                                <div className={`p-6 h-100 ${i < 3 ?"border-end border-white border-opacity-25" : ""}`}>
                                  <div className="d-flex align-items-center">
                                    <div className="symbol symbol-45px me-4">
                                      <span className="symbol-label bg-white bg-opacity-15">
                                        <IconComponent className="w-6 h-6 text-white" />
                                      </span>
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="text-white fw-bold fs-7">{item.label}</div>
                                      <div className="fs-2hx fw-bold text-white">{item.value}</div>
                                      <div className="text-white text-opacity-75">{item.sub}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Main Layout: Col-8 (Incidents) and Col-4 (Access/Overstay) */}
                    <div className="row g-5">
                      {/* Security Incidents (col-8) */}
                      <div className="col-12 col-lg-8">
                        <div className="card card-flush h-100">
                          <div className="card-header pt-7 pb-5 d-flex align-items-center flex-wrap gap-4 border-bottom">
                            <h3 className="card-title align-items-start flex-column m-0">
                              <span className="card-label fw-bold text-gray-900 fs-4">Reported Security Incidents</span>
                              <span className="text-gray-500 fs-7">Review and manage reported security breaches and concerns.</span>
                            </h3>

                            <div className="d-flex align-items-center gap-3 flex-wrap flex-sm-nowrap ms-auto">
                              <div className="position-relative" style={{ width: 220 }}>
                                <i className="ki-outline ki-magnifier fs-3 position-absolute ms-4 top-50 translate-middle-y"></i>
                                <input className="form-control form-control-solid form-control-sm ps-12 w-100" placeholder="Search incident..." value={incidentFilters.search} onChange={e => setIncidentFilters(p => ({ ...p, search: e.target.value }))} />
                              </div>
                              <select className="form-select form-select-solid form-select-sm" style={{ width: 150 }} value={incidentFilters.status} onChange={e => setIncidentFilters(p => ({ ...p, status: e.target.value }))}>
                                <option value="all">Status: All</option>
                                <option value="Created">Reported</option>
                                <option value="In Progress">Investigating</option>
                                <option value="Completed">Resolved</option>
                                <option value="Closed">Closed</option>
                              </select>
                            </div>
                          </div>

                          <div className="card-body pt-4">
                            {incidentsLoading ? <div className="text-center py-10"><span className="spinner-border text-danger"></span></div> :
                            incidents.length === 0 ? (
                              <div className="text-center py-15 text-gray-500">
                                <i className="ki-outline ki-shield-cross fs-5x text-gray-300 d-block mb-4"></i>
                                No security incidents reported.
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-row-dashed align-middle gs-0 gy-4">
                                  <thead>
                                    <tr className="fw-bold text-muted text-uppercase fs-8">
                                      <th className="ps-4 min-w-200px">Report</th>
                                      <th>Reporter / Loc</th>
                                      <th>Priority</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {incidents.map(inc => {
                                      const sm = STATUS_META[inc.status] || STATUS_META["Created"];
                                      const pm = PRIORITY_META[inc.priorityLevel || inc.priority] || PRIORITY_META["Normal"];
                                      return (
                                        <tr key={inc.id} className="cursor-pointer bg-hover-light" onClick={() => { setSelectedIncident(inc); incidentModal.show(); }}>
                                          <td className="ps-4">
                                            <div className="d-flex align-items-center gap-3">
                                              <div className="symbol symbol-35px symbol-circle flex-shrink-0">
                                                <span className="symbol-label bg-light-danger"><i className="ki-outline ki-shield-cross text-danger fs-3"></i></span>
                                              </div>
                                              <div className="d-flex flex-column">
                                                <span className="text-gray-900 fw-bold mb-1 text-truncate mw-200px" title={inc.title}>{inc.title || "Security Alert"}</span>
                                                <span className="fs-8 text-muted">{inc.category}</span>
                                              </div>
                                            </div>
                                          </td>
                                          <td>
                                            <div className="text-gray-900 fw-semibold fs-7">{inc.requesterName || "Anonymous"}</div>
                                            <div className="text-gray-500 fs-8">{inc.buildingNumber || "—"}</div>
                                          </td>
                                          <td><span className={`badge fs-8 ${pm.badge}`}>{inc.priorityLevel || inc.priority || "Normal"}</span></td>
                                          <td><span className={`badge ${sm.badge}`}>{sm.label}</span></td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Active / Overtime Visits (col-4) */}
                      <div className="col-12 col-lg-4">
                        <div className="card card-flush h-100 border border-gray-300">
                          <div className="card-header pt-7 pb-4 d-flex flex-column">
                            <h3 className="card-title align-items-start flex-column m-0 mb-2">
                              <span className="card-label fw-bold text-gray-900 fs-4">Active &amp; Overtime Stays</span>
                              <span className="text-gray-500 fs-7">
                                {accessTotals.overstays > 0 ? `${accessTotals.overstays} overstayed · ` : ""}
                                {accessTotals.inside} inside{accessTotals.inside > activeLogs.length ? ` · showing ${activeLogs.length}` : ""}
                              </span>
                            </h3>
                            <div className="position-relative w-100 mt-2">
                              <i className="ki-outline ki-magnifier fs-6 position-absolute ms-3 top-50 translate-middle-y"></i>
                              <input className="form-control form-control-solid form-control-sm ps-10" placeholder="Search active visitors..." value={accessSearch} onChange={e => setAccessSearch(e.target.value)} />
                            </div>
                          </div>

                          <div className="card-body p-0">
                            {accessLoading ? (
                              <div className="text-center py-10"><span className="spinner-border text-success"></span></div>
                            ) : activeLogs.length === 0 ? (
                              <div className="text-center py-10 text-gray-500 fs-7">
                                No active visitors currently inside.
                              </div>
                            ) : (
                              <div className="d-flex flex-column mh-400px overflow-y-auto px-6 pb-6 gap-3">
                                {activeLogs.map(log => {
                                  const isOvertime = checkIsOverstay(log, passInfo);
                                  return (
                                    <div 
                                      key={log.id} 
                                      onClick={() => handleAccessLogClick(log)}
                                      className={`d-flex align-items-center p-3 rounded border cursor-pointer transition ${isOvertime ?'border-danger bg-light-danger' : 'border-gray-200 bg-hover-light'}`}
                                    >
                                      <div className="symbol symbol-40px symbol-circle me-3 flex-shrink-0">
                                        <span className={`symbol-label fw-bold ${isOvertime ?'bg-danger text-white' : 'bg-light-success text-success'}`}>
                                          {getInitials(log.dependentName || log.visitorName || "V")}
                                        </span>
                                      </div>
                                      <div className="d-flex flex-column flex-grow-1">
                                        <span className="fw-bold text-gray-900 fs-6 text-truncate" style={{maxWidth: '150px'}}>{log.dependentName || log.visitorName || "Unknown"}</span>
                                        <span className="text-muted fs-8 d-flex align-items-center gap-1">
                                          <i className="ki-outline ki-time fs-8"></i> In: {fmtCheckIn(log.checkedInAt)} · {officerLabel(log, "in")}
                                        </span>
                                      </div>
                                      {isOvertime && (
                                        <span className="badge badge-danger fs-9 ms-2 px-2 py-1">OVERSTAY</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                    </div>

                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                  <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                    <div className="text-gray-900 text-center">
                      <span className="text-muted fw-semibold me-1">2026&copy;</span>
                      <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>

      <GateActionModal modalRef={checkInModalRef} actionType="in" estateId={estateId} onSuccess={fetchActiveLogs} />
      <GateActionModal modalRef={checkOutModalRef} actionType="out" estateId={estateId} onSuccess={fetchActiveLogs} />
      
      <IncidentDetailModal modalRef={incidentModalRef} estateId={estateId} request={selectedIncident} onStatusChange={handleIncidentStatusChange} />
      <AccessDetailModal 
        modalRef={accessDetailModalRef} 
        selectedItem={selectedAccessLog} 
        passInfo={passInfo}
        owner={{
          id: selectedAccessLog?.tenantId || selectedAccessLog?.hostId,
          fullName: selectedAccessLog?.tenantFullName || selectedAccessLog?.hostName || "Host Tenant",
          phone: selectedAccessLog?.tenantPhone || selectedAccessLog?.hostPhoneNumber || selectedAccessLog?.hostPhone || null,
          avatarUrl: selectedAccessLog?.tenantImageUrl || selectedAccessLog?.hostImageUrl || null,
          building: selectedAccessLog?.tenantBuilding || selectedAccessLog?.hostBuilding || "—",
        }} 
      />
    </>
  );
}